import React from "react";
import { Text, Flex, Link, Tag } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { Colors } from "@jsq/ds/styles";
import { type GetChildWorkItemsQuery } from "#graphql/supergraphTypes";
import { ControlTowerContext } from "#utils/context";
import { SPACING } from "#utils/spacing";
import { useControlTowerContext } from "../../ControlTowerContext";
import * as styles from "./styles_vanilla.css";
import {
  extractDescriptionAfterColon,
  formatChildWorkItemStatus,
  getStatusTagVariant,
  isTaskCompleted,
} from "./utils";

type TasksTableProps = {
  tasks: GetChildWorkItemsQuery["childWorkItems"]["items"][number][];
  jiraBaseURL: string;
};

export const TasksTable: React.FC<TasksTableProps> = ({ tasks, jiraBaseURL }) => {
  const context = useControlTowerContext();
  const isCanopy = context === ControlTowerContext.CANOPY;
  const isGPX = context === ControlTowerContext.GPX;

  return (
    <div className={styles.tasksCard}>
      <div className={styles.tasksCardHeader}>
        <Text variant="h3" className={styles.tasksTitle}>
          Tasks
        </Text>
      </div>
      <div className={styles.taskTableScroll}>
        <table className={styles.taskTable}>
          <thead>
            <tr className={styles.taskTableHeaderRow}>
              <th className={styles.taskTableHeaderName} colSpan={1}>
                <Text variant="regular-md" className={styles.taskTableHeaderText}>
                  Name
                </Text>
              </th>
              <th className={styles.taskTableHeader}>
                <Text variant="regular-md" className={styles.taskTableHeaderText}>
                  Responsible party
                </Text>
              </th>
              {!isGPX && (
                <th className={styles.taskTableHeader}>
                  <Text variant="regular-md" className={styles.taskTableHeaderText}>
                    Assigned
                  </Text>
                </th>
              )}
              <th className={styles.taskTableHeader}>
                <Text variant="regular-md" className={styles.taskTableHeaderText}>
                  Status
                </Text>
              </th>
              {isCanopy && <th className={styles.taskTableHeaderAction}></th>}
            </tr>
          </thead>
          <tbody>
            {tasks.map((task, index) => {
              const effectiveStatus = String(
                isCanopy ? task.status : (task.statusCategoryName ?? task.status ?? ""),
              );
              const completed = isTaskCompleted(effectiveStatus);
              return (
                <tr key={task.key} className={styles.taskTableRow}>
                  <td
                    className={
                      isGPX
                        ? `${styles.taskTableCellNameStep} ${styles.taskTableCellBorderGPX}`
                        : styles.taskTableCellNameStep
                    }
                  >
                    <Flex alignItems="center" gap={SPACING.SMALL}>
                      <Flex
                        alignItems="center"
                        justifyContent="center"
                        className={
                          completed
                            ? styles.stepIndicatorComplete
                            : styles.stepIndicatorIncomplete
                        }
                      >
                        {completed ? (
                          <Icon
                            type="check"
                            variant="outline"
                            color={Colors.Neutral0}
                            size="small"
                          />
                        ) : (
                          <Text variant="strong-sm" className={styles.stepIndicatorText}>
                            {index + 1}
                          </Text>
                        )}
                      </Flex>
                      <Text variant="regular-md" color={Colors.Neutral900}>
                        {task.summary ? extractDescriptionAfterColon(task.summary) : "-"}
                      </Text>
                    </Flex>
                  </td>
                  <td
                    className={
                      isGPX
                        ? `${styles.taskTableCell} ${styles.taskTableCellBorderGPX}`
                        : styles.taskTableCell
                    }
                  >
                    <Text variant="regular-md" color={Colors.Neutral900}>
                      {task.fundAdministrationDepartment || "-"}
                    </Text>
                  </td>
                  {!isGPX && (
                    <td className={styles.taskTableCell}>
                      <Text variant="regular-md" color={Colors.Neutral900}>
                        {task.assignedTo?.displayName || "Unassigned"}
                      </Text>
                    </td>
                  )}
                  <td
                    className={
                      isGPX
                        ? `${styles.taskTableCellStatus} ${styles.taskTableCellBorderGPX}`
                        : styles.taskTableCellStatus
                    }
                  >
                    <Tag
                      variant={getStatusTagVariant(effectiveStatus)}
                      label={formatChildWorkItemStatus(effectiveStatus)}
                    />
                  </td>
                  {isCanopy && (
                    <td className={styles.taskTableCellAction}>
                      <Link
                        href={`${jiraBaseURL}/browse/${task.key}`}
                        shouldOpenInNewTab
                        icon="external-link"
                        iconPosition="right"
                        iconVariant="outline"
                        iconSize="small"
                        align="left"
                      >
                        {task.key}
                      </Link>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};
